export function initializeReaderNavigation(root: Document = document): void {
  const rail = root.querySelector<HTMLElement>("[data-reader-navigation]");
  const contents = rail?.querySelector<HTMLDetailsElement>(".page-contents");
  const summary = contents?.querySelector<HTMLElement>("summary");
  if (!rail || !contents || !summary || rail.dataset.readerNavigationReady) return;
  rail.dataset.readerNavigationReady = "true";

  const desktopHover = matchMedia("(min-width: 74rem) and (hover: hover)");
  let transient = false;
  let dismissed = false;
  let closeTimer: ReturnType<typeof setTimeout> | undefined;

  const cancelClose = () => { if (closeTimer) clearTimeout(closeTimer); };
  const openTemporarily = () => {
    cancelClose();
    if (contents.open || dismissed) return;
    transient = true;
    contents.open = true;
  };
  const closeTemporarily = () => {
    cancelClose();
    closeTimer = window.setTimeout(() => {
      if (transient && !contents.matches(":hover") && !contents.contains(root.activeElement)) {
        contents.open = false;
        transient = false;
      }
    }, 160);
  };

  contents.addEventListener("pointerenter", () => { if (desktopHover.matches) openTemporarily(); });
  contents.addEventListener("pointerleave", () => { dismissed = false; closeTemporarily(); });
  contents.addEventListener("focusin", () => { if (summary.matches(":focus-visible")) openTemporarily(); });
  contents.addEventListener("focusout", () => { dismissed = false; closeTemporarily(); });
  summary.addEventListener("click", event => {
    cancelClose();
    if (transient && contents.open) {
      event.preventDefault();
      transient = false;
      dismissed = false;
      return;
    }
    transient = false;
    dismissed = contents.open;
  });
  contents.addEventListener("click", event => {
    if (!(event.target instanceof Element) || !event.target.closest("a")) return;
    transient = false;
    dismissed = true;
    contents.open = false;
  });
  root.addEventListener("keydown", event => {
    if (event.key !== "Escape" || !contents.open) return;
    dismissed = true;
    transient = false;
    contents.open = false;
    if (contents.contains(root.activeElement)) summary.focus({ preventScroll: true });
  }, true);
  root.addEventListener("pointerdown", event => {
    if (!(event.target instanceof Node) || contents.contains(event.target)) return;
    contents.open = false;
    transient = false;
  });

  const headings = [...contents.querySelectorAll<HTMLAnchorElement>('a[href^="#node-"]')]
    .map(link => ({ link, heading: root.getElementById(link.hash.slice(1)) }))
    .filter((entry): entry is { link: HTMLAnchorElement; heading: HTMLElement } => entry.heading !== null);
  let active: HTMLAnchorElement | undefined;
  let frame = 0;
  const updateCurrent = () => {
    frame = 0;
    const threshold = Math.max(100, innerHeight * 0.25);
    let current = headings[0];
    for (const entry of headings) {
      if (entry.heading.getBoundingClientRect().top > threshold) break;
      current = entry;
    }
    if (!current || active === current.link) return;
    active?.removeAttribute("aria-current");
    active = current.link;
    active.setAttribute("aria-current", "location");
    summary.title = `Contents · ${active.textContent?.trim() || "Current section"}`;
  };
  const scheduleCurrent = () => { if (!frame) frame = requestAnimationFrame(updateCurrent); };
  const observer = new IntersectionObserver(scheduleCurrent, { rootMargin: "-15% 0px -70% 0px" });
  headings.forEach(({ heading }) => observer.observe(heading));
  window.addEventListener("scroll", scheduleCurrent, { passive: true });
  window.addEventListener("resize", scheduleCurrent, { passive: true });
  window.addEventListener("pagehide", () => { observer.disconnect(); if (frame) cancelAnimationFrame(frame); });
  updateCurrent();
}
